import React, { useMemo } from 'react';
import styled from 'styled-components';
import { colors, fonts } from '../../components/annuaire/designSystem';
import {
  heure,
  horaireCourt,
  jourLong,
  joursCombines,
  libellePastille,
  majuscule,
  plusTot,
  toutesLesHeures,
} from './creneaux';
import {
  BoutonVert,
  Corps,
  Encart,
  LARGE,
  PetitTexte,
  Pied,
  SousTitre,
  Titre,
  mobile,
} from './ui';

/**
 * Étape 2, atelier en RÉSERVATION INSTANTANÉE : le client choisit l'heure
 * exacte, et elle est réservée (forme I3 retenue le 21/09).
 *
 * Le premier horaire libre en tête, puis tous les jours ouverts à la suite,
 * sans rien replier. Un seul horaire : le choisi est coché, les autres
 * s'estompent (option 2 du 21/09) mais restent cliquables ; en toucher un
 * autre remplace le choix.
 *
 * Une heure complète (`full_slots`, FIXERLIGHT-053) apparaît grisée dans la
 * même grille, non cliquable, libellée « Complet » — distincte d'une heure
 * hors horaires qui n'apparaît pas du tout (FIXERLIGHT-070).
 */

const Tot = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 18px;
  border-radius: 16px;
  background: ${colors.bleuFonce};
  color: ${colors.white};
  opacity: ${(p) => (p.$estompe ? 0.4 : 1)};

  small {
    display: block;
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${colors.vert};
  }
  strong {
    font-family: ${fonts.heading};
    font-size: 19px;
  }
  button {
    margin-left: auto;
    min-height: 44px;
    padding: 0 16px;
    border: none;
    border-radius: 50px;
    background: ${colors.vert};
    color: ${colors.textDark};
    font-family: ${fonts.body};
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
  }
`;

// Une div et non une section : le site donne 80 px de marge et de retrait à
// toutes les `section` (feuille de style globale).
const Jour = styled.div`
  margin-bottom: 16px;

  h3 {
    margin: 0 0 8px;
    font-family: ${fonts.body};
    font-size: 14px;
    font-weight: 600;
    color: ${colors.textDark};
  }
`;

const Heures = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  @media (min-width: ${LARGE}) {
    grid-template-columns: repeat(5, 1fr);
  }
`;

const Heure = styled.button`
  min-height: 48px;
  border-radius: 12px;
  border: 1.5px solid ${(p) => (p.$complet ? mobile.border : colors.bleu)};
  background: ${(p) =>
    p.$complet ? mobile.muted : p.$on ? colors.bleu : colors.white};
  color: ${(p) =>
    p.$complet ? mobile.mutedText : p.$on ? colors.white : colors.bleu};
  opacity: ${(p) => (p.$estompe ? 0.4 : 1)};
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
  cursor: ${(p) => (p.$complet ? 'default' : 'pointer')};

  &:disabled {
    opacity: ${(p) => (p.$estompe ? 0.4 : 1)};
  }
`;

/** « Complet », sous l'heure, dans un bloc grisé. */
const Complet = styled.span`
  display: block;
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

export default function EtapeHoraire({
  slots,
  fullSlots = [],
  choisi,
  onChoisir,
  alerte,
  onContinuer,
  titreRef,
  dureeMin,
}) {
  const jours = useMemo(() => joursCombines(slots, fullSlots), [
    slots,
    fullSlots,
  ]);
  const premier = plusTot(slots);
  // FIXERLIGHT-070 (relecture PR#181, B1) : la grille peut maintenant montrer
  // des heures « Complet » — le sous-titre ne peut plus prétendre que tout ce
  // qui est affiché est libre dès qu'un jour en porte au moins une.
  const aDesComplets = jours.some((j) => j.creneauxComplets.length > 0);

  return (
    <>
      <Corps>
        <Titre tabIndex={-1} ref={titreRef}>
          À quelle heure ?
        </Titre>
        <SousTitre>
          {aDesComplets
            ? 'Les horaires marqués « Complet » sont déjà pris.'
            : 'Tous les horaires affichés sont libres.'}
          {dureeMin
            ? ` Durée prévue : ${
                dureeMin >= 60 ? `${dureeMin / 60} h` : `${dureeMin} min`
              }.`
            : ''}
        </SousTitre>
        {alerte && (
          <Encart $ton="alerte" role="alert">
            {alerte}
          </Encart>
        )}

        {premier && (
          <Tot $estompe={!!choisi && choisi !== premier}>
            <div>
              <small>Le plus tôt</small>
              <strong>
                {libellePastille(premier)}, {heure(premier)}
              </strong>
            </div>
            <button
              type="button"
              aria-label={`Réserver le plus tôt : ${libellePastille(
                premier,
              )}, ${heure(premier)}`}
              onClick={() => onChoisir(premier, true)}>
              Réserver
            </button>
          </Tot>
        )}

        {jours.map((j) => {
          const premierIsoDuJour = j.creneaux[0] || j.creneauxComplets[0];
          return (
            <Jour
              key={j.cle}
              role="group"
              aria-label={jourLong(premierIsoDuJour)}>
              <h3>{majuscule(jourLong(premierIsoDuJour))}</h3>
              <Heures>
                {toutesLesHeures(j).map(({ iso, complet }) => {
                  const on = iso === choisi;
                  return (
                    <Heure
                      key={iso}
                      type="button"
                      $on={on}
                      $complet={complet}
                      $estompe={!complet && !!choisi && !on}
                      disabled={complet}
                      aria-pressed={complet ? undefined : on}
                      aria-label={
                        complet
                          ? `${heure(iso)}, complet`
                          : `${heure(iso)}, ${jourLong(iso)}`
                      }
                      onClick={() => !complet && onChoisir(iso)}>
                      {complet ? (
                        <>
                          {heure(iso)}
                          <Complet>Complet</Complet>
                        </>
                      ) : (
                        <>
                          {on ? '✓ ' : ''}
                          {heure(iso)}
                        </>
                      )}
                    </Heure>
                  );
                })}
              </Heures>
            </Jour>
          );
        })}
      </Corps>
      <Pied>
        <BoutonVert type="button" disabled={!choisi} onClick={onContinuer}>
          {choisi
            ? `Réserver ${horaireCourt(choisi)}`
            : 'Choisissez un horaire'}
        </BoutonVert>
        <PetitTexte>
          Confirmé instantanément, sans attendre de réponse de l’atelier.
        </PetitTexte>
      </Pied>
    </>
  );
}
